/**
 * usePullToRefresh Hook
 * Gestisce il pull-to-refresh per interfacce mobile
 */

import { useState, useRef, useCallback, useEffect } from 'react';

export const usePullToRefresh = (onRefresh, options = {}) => {
  const {
    threshold = 80,
    maxPullDistance = 120,
    resistanceRatio = 0.5,
    refreshingDuration = 1500,
    disabled = false
  } = options;

  // Stati
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [shouldTrigger, setShouldTrigger] = useState(false);

  // Refs
  const containerRef = useRef(null);
  const startYRef = useRef(0);
  const startXRef = useRef(0);
  const currentYRef = useRef(0);
  const isPullingRef = useRef(false);
  const activeScrollElementRef = useRef(null);

  // Calcolazioni per animazioni
  const pullProgress = Math.min(pullDistance / threshold, 1);
  const iconRotation = pullProgress * 180;
  const iconOpacity = Math.min(pullProgress * 1.5, 1);
  const iconScale = 0.8 + (pullProgress * 0.4);

  // Reset dello stato
  const resetState = useCallback(() => {
    setIsPulling(false);
    setPullDistance(0);
    setShouldTrigger(false);
    isPullingRef.current = false;
    startYRef.current = 0;
    startXRef.current = 0;
    currentYRef.current = 0;
    activeScrollElementRef.current = null;
  }, []);

  // Utility: trova l'antenato scrollabile più vicino a partire dal nodo
  const findNearestScrollableAncestor = useCallback((node) => {
    if (!node) return null;
    let currentNode = node;
    while (currentNode && currentNode !== document.body && currentNode !== document.documentElement) {
      try {
        const style = window.getComputedStyle(currentNode);
        const overflowY = style.overflowY;
        const isScrollableByStyle = overflowY === 'auto' || overflowY === 'scroll';
        const canScrollBySize = currentNode.scrollHeight > currentNode.clientHeight + 1;
        if ((isScrollableByStyle || canScrollBySize) && typeof currentNode.scrollTop === 'number') {
          return currentNode;
        }
      } catch (e) {
        // Ignora nodi con accesso stile non disponibile
      }
      currentNode = currentNode.parentElement;
    }
    return null;
  }, []);

  // Gestori touch events
  const onTouchStart = useCallback((e) => {
    if (disabled || isRefreshing) return;

    const container = containerRef.current;
    if (!container) return;

    // Registra le coordinate iniziali
    startYRef.current = e.touches[0].clientY;
    startXRef.current = e.touches[0].clientX;
    currentYRef.current = startYRef.current;

    // Trova l'antenato scrollabile più vicino al punto di contatto
    const nearestScrollable = findNearestScrollableAncestor(e.target) || container;
    activeScrollElementRef.current = nearestScrollable;

    // Deve essere in cima per poter iniziare
    const scrollTop = nearestScrollable ? nearestScrollable.scrollTop : container.scrollTop;
    const isAtTop = scrollTop <= 2; // Piccolo buffer per tolleranza
    if (!isAtTop) {
      // Non iniziare tracciamento per evitare attivazioni a metà pagina
      resetState();
      return;
    }
    // Non impostiamo ancora isPulling - aspettiamo di vedere il movimento
  }, [disabled, isRefreshing, findNearestScrollableAncestor, resetState]);

  const onTouchMove = useCallback((e) => {
    if (disabled || isRefreshing) return;

    const container = containerRef.current;
    if (!container) return;

    // Se non abbiamo coordinate di partenza, ignora
    if (startYRef.current === 0) return;

    currentYRef.current = e.touches[0].clientY;
    const currentX = e.touches[0].clientX;
    const deltaY = currentYRef.current - startYRef.current;
    const deltaX = Math.abs(currentX - startXRef.current);

    // Evita di interferire con gesture orizzontali (es. carousels)
    if (deltaX > Math.abs(deltaY) && deltaX > 10) {
      resetState();
      return;
    }

    // Verifica CONTINUAMENTE che siamo ancora in cima sull'elemento scrollabile attivo
    const scrollEl = activeScrollElementRef.current || container;
    const isStillAtTop = (scrollEl ? scrollEl.scrollTop : container.scrollTop) <= 2;

    if (deltaY > 0 && isStillAtTop) {
      // Movimento verso il basso E siamo in cima
      
      // Soglia minima per attivare il pull-to-refresh (10px)
      if (deltaY >= 10) {
        // Ora siamo sicuri che è un gesto di pull-to-refresh
        if (!isPullingRef.current) {
          setIsPulling(true);
          isPullingRef.current = true;
        }

        // Previene lo scroll naturale solo ora che siamo sicuri
        e.preventDefault();

        // Calcola la distanza con resistenza
        const adjustedDelta = deltaY - 10; // Sottrai la soglia iniziale
        const resistance = Math.pow(adjustedDelta / maxPullDistance, resistanceRatio);
        const newPullDistance = Math.min(adjustedDelta * resistance, maxPullDistance);
        
        setPullDistance(newPullDistance);
        setShouldTrigger(newPullDistance >= threshold);
      }
    } else if (deltaY <= 0 || !isStillAtTop) {
      // Movimento verso l'alto O non siamo più in cima - reset
      resetState();
    }
  }, [disabled, isRefreshing, maxPullDistance, resistanceRatio, threshold, resetState]);

  const onTouchEnd = useCallback(async () => {
    // Se non abbiamo iniziato a trackare il movimento, reset e basta
    if (startYRef.current === 0) {
      resetState();
      return;
    }

    if (!isPullingRef.current || disabled) {
      resetState();
      return;
    }

    if (shouldTrigger && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(threshold); // Mantieni la posizione durante il refresh
      
      try {
        await onRefresh();
      } catch (error) {
        console.error('Errore durante il refresh:', error);
      }
      
      // Aspetta la durata minima del refresh
      setTimeout(() => {
        setIsRefreshing(false);
        resetState();
      }, refreshingDuration);
    } else {
      resetState();
    }
  }, [disabled, shouldTrigger, isRefreshing, onRefresh, threshold, refreshingDuration, resetState]);

  // Cleanup su unmount
  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  return {
    // Touch handlers
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    
    // Refs
    containerRef,
    
    // Stati
    isPulling,
    isRefreshing,
    pullDistance,
    shouldTrigger,
    
    // Valori per animazioni
    pullProgress,
    iconRotation,
    iconOpacity,
    iconScale
  };
};
